import { useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { ChevronDown, Zap } from "lucide-react";
import { formatNumber, classNames } from "@/lib/format";
import MediaImage from "@/components/MediaImage";
import { explicitDemoMedia } from "@/lib/media";

function ChangeBadge({ cluster }) {
  if (cluster.is_new) return <span className="text-[10px] font-bold uppercase tracking-wider rounded-full bg-primary/10 text-primary px-2 py-0.5">New</span>;
  if (cluster.change_pct === null || cluster.change_pct === undefined) return null;
  const up = cluster.change_pct >= 0;
  return (
    <span className={classNames("font-mono text-[11px] font-semibold", up ? "text-[hsl(var(--emerald))]" : "text-[hsl(var(--rose))]")}>
      {up ? "▲" : "▼"} {Math.abs(cluster.change_pct)}%
    </span>
  );
}

export default function TrendCluster({ title, subtitle, clusters, accent = "rose", testid }) {
  const [open, setOpen] = useState(null);
  const max = Math.max(...clusters.map((c) => c.heat), 1);
  const bar = accent === "rose" ? "bg-[hsl(var(--rose))]" : "bg-[hsl(var(--amber))]";

  return (
    <Card className="p-5" data-testid={testid}>
      <h3 className="font-display font-bold text-lg mb-1">{title}</h3>
      <p className="text-xs text-muted-foreground mb-4">{subtitle}</p>
      <div className="space-y-1">
        {clusters.map((c, i) => {
          const expanded = open === c.name;
          return (
            <div key={c.name} className="rounded-lg border border-transparent hover:border-border transition-colors">
              <button
                onClick={() => setOpen(expanded ? null : c.name)}
                className="w-full text-left px-3 py-2.5"
                data-testid={`${testid}-row-${i}`}
              >
                <div className="flex items-center gap-3">
                  <span className="font-mono text-xs text-muted-foreground w-4">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold truncate">{c.name}</span>
                      <ChangeBadge cluster={c} />
                      {c.outliers > 0 && (
                        <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-[hsl(var(--emerald))]">
                          <Zap className="h-3 w-3" />{c.outliers}
                        </span>
                      )}
                    </div>
                    <div className="mt-1.5 h-1.5 w-full rounded-full bg-muted overflow-hidden">
                      <div className={classNames("h-full rounded-full transition-[width] duration-500", bar)} style={{ width: `${Math.max((c.heat / max) * 100, 3)}%` }} />
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-mono text-sm font-bold">{formatNumber(c.views)}</div>
                    <div className="text-[10px] text-muted-foreground">{c.count} videos · {c.avg_multiplier}x avg</div>
                  </div>
                  <ChevronDown className={classNames("h-4 w-4 text-muted-foreground transition-transform", expanded && "rotate-180")} />
                </div>
              </button>
              {expanded && (
                <div className="px-3 pb-3 pl-10 space-y-1.5" data-testid={`${testid}-expanded-${i}`}>
                  {c.top_videos.map((v) => (
                    <Link key={v.id} to={`/videos/${v.id}`} className="flex items-center gap-3 rounded-md bg-muted/40 px-2 py-1.5 hover:bg-muted transition-colors">
                      <MediaImage src={v.thumbnail} alt="" demo={explicitDemoMedia(v)} showLabel={false} className="h-9 w-7 rounded object-cover" />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium truncate">{v.title}</div>
                        <div className="text-[10px] text-muted-foreground">{v.creator_handle}</div>
                      </div>
                      <div className="font-mono text-xs">{formatNumber(v.views)}</div>
                      <div className={classNames("font-mono text-xs font-bold", v.is_outlier ? "text-[hsl(var(--emerald))]" : "text-muted-foreground")}>{v.outlier_multiplier.toFixed(1)}x</div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}
